import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Skeleton } from "@nexus/ui";
import type { BigQueryUsageStats } from "@/features/bigquery-usage/api/bigquery-usage.api";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import { formatMicrosAsUsd } from "@/features/llm-usage/lib/money";
import { formatBytesCompact, formatBytesExact } from "../lib/bytes";

interface BigQueryKpiStripProps {
  stats: BigQueryUsageStats | undefined;
  isLoading: boolean;
  /** A read that failed knows nothing about usage — see R33. */
  isError: boolean;
  onRetry: () => void;
  /** These numbers still belong to the previous window; the next is in flight. */
  isStale: boolean;
  /** Narrows the tab to the queries no tenant was recorded for. */
  onSelectUnattributed: () => void;
}

const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";
const VALUE_CLASS = "text-lg font-semibold leading-7 tabular-nums";
/** A zero reports quietly and lets a real number carry the weight. */
const ZERO_VALUE_CLASS = "text-lg font-normal leading-7 tabular-nums text-muted-foreground";
const SECONDARY_CLASS = "text-[11px] text-muted-foreground";
const STAT_CLASS = "flex flex-col gap-0.5 p-1.5 text-left";
const SKELETON_WIDTHS = ["w-20", "w-24", "w-20", "w-16", "w-16", "w-20"];
const STRIP_CLASS = "-mx-1.5 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3";
const DASH = "—";

const PERCENT_FRACTION_DIGITS = 1;
const FRACTION_TO_PERCENT = 100;
/** Slot hours are read as a load figure, where a second decimal is noise. */
const SLOT_HOURS_FRACTION_DIGITS = 1;

function Stat({ label, title, children }: { label: string; title?: string; children: ReactNode }) {
  return (
    <div className={STAT_CLASS} title={title}>
      <span className={LABEL_CLASS}>{label}</span>
      <span className="flex items-baseline gap-1.5">{children}</span>
    </div>
  );
}

/**
 * What the window cost, how much it scanned, and how hard it was.
 *
 * Two numbers here carry a claim the API is careful about and the strip has to
 * keep: a null cost is an unset rate rather than a free window, and slot hours
 * are load rather than a second bill.
 */
export function BigQueryKpiStrip({
  stats,
  isLoading,
  isError,
  onRetry,
  isStale,
  onSelectUnattributed,
}: BigQueryKpiStripProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // Checked ahead of the skeleton on purpose. R33: a read that failed is not a
  // read still in flight, and leaving the skeleton up says it is.
  if (isError) {
    return (
      <div className={cn(STRIP_CLASS, "gap-x-3")}>
        <span className="flex items-center gap-1.5 px-1.5 text-sm text-muted-foreground">
          {/* The icon, not the colour, is what says this failed. */}
          <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
          {t("admin.bigQueryUsage.kpi.error", "Could not load the BigQuery usage totals.")}
        </span>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t("admin.bigQueryUsage.retry", "Try again")}
        </Button>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className={STRIP_CLASS}>
        {SKELETON_WIDTHS.map((width, index) => (
          <Skeleton key={index} className={cn("h-11 rounded-sm bg-foreground/8", width)} />
        ))}
      </div>
    );
  }

  const hasTraffic = stats.queries > 0;
  const isUnpriced = stats.costMicros == null;
  const cacheHitPercent =
    stats.cacheHitRate == null
      ? null
      : (stats.cacheHitRate * FRACTION_TO_PERCENT).toLocaleString(locale, {
          maximumFractionDigits: PERCENT_FRACTION_DIGITS,
        });

  return (
    <div
      className={cn(
        STRIP_CLASS,
        "[&>*]:transition-opacity [&>*]:duration-200",
        isStale && "[&>*]:opacity-40",
      )}
      aria-busy={isStale || undefined}
    >
      <Stat label={t("admin.bigQueryUsage.kpi.queries", "Queries")}>
        <span className={hasTraffic ? VALUE_CLASS : ZERO_VALUE_CLASS}>
          {stats.queries.toLocaleString(locale)}
        </span>
      </Stat>

      <Stat
        label={t("admin.bigQueryUsage.kpi.bytesBilled", "Bytes billed")}
        title={formatBytesExact(stats.bytesBilled, locale)}
      >
        <span className={hasTraffic ? VALUE_CLASS : ZERO_VALUE_CLASS}>
          {formatBytesCompact(stats.bytesBilled, locale)}
        </span>
        <span className={SECONDARY_CLASS}>
          {t("admin.bigQueryUsage.kpi.bytesProcessed", "{{bytes}} scanned", {
            bytes: formatBytesCompact(stats.bytesProcessed, locale),
          })}
        </span>
      </Stat>

      {/* The one number nobody has to convert before taking it to a meeting. It
          exists only if somebody has set the price per TB, and saying `$0.00`
          when nobody has is the wrong claim: the cost is unknown, not zero. */}
      <Stat label={t("admin.bigQueryUsage.kpi.cost", "Est. cost")}>
        <span className={isUnpriced ? ZERO_VALUE_CLASS : VALUE_CLASS}>
          {isUnpriced ? DASH : formatMicrosAsUsd(stats.costMicros ?? 0, locale)}
        </span>
        {isUnpriced ? (
          <span
            className={SECONDARY_CLASS}
            title={t(
              "admin.bigQueryUsage.kpi.costUnpricedTitle",
              "No price per TB is set, so this window cannot be costed. Set it in Admin → Settings → Operations.",
            )}
          >
            {t("admin.bigQueryUsage.kpi.costUnpriced", "no price per TB set")}
          </span>
        ) : hasTraffic ? (
          // R30 rides on the figure it qualifies, the way the unpriced note does.
          // Bytes are recorded; a price never is, so every cost here is computed
          // at today's rate — including for windows that closed weeks ago.
          <span
            className={SECONDARY_CLASS}
            title={t(
              "admin.bigQueryUsage.kpi.rateNote",
              "Costs use the price per TB set now, so changing that rate restates past windows too.",
            )}
          >
            {t("admin.bigQueryUsage.kpi.rateNoteShort", "at today's rate")}
          </span>
        ) : null}
      </Stat>

      <Stat
        label={t("admin.bigQueryUsage.kpi.cacheHits", "Cache hits")}
        title={t("admin.bigQueryUsage.kpi.cacheHitsTitle", "{{hits}} of {{queries}} queries", {
          hits: stats.cacheHits.toLocaleString(locale),
          queries: stats.queries.toLocaleString(locale),
        })}
      >
        <span className={cacheHitPercent == null ? ZERO_VALUE_CLASS : VALUE_CLASS}>
          {/* Null is a window with no queries to take a rate over. Dividing here
              is what would produce the NaN, and 0% would say every query missed. */}
          {cacheHitPercent == null ? DASH : `${cacheHitPercent}%`}
        </span>
        {cacheHitPercent == null ? (
          <span className={SECONDARY_CLASS}>
            {t("admin.bigQueryUsage.kpi.cacheHitsNoQueries", "no queries in this window")}
          </span>
        ) : null}
      </Stat>

      {/* Slot time is a load signal: this project has no slot reservation, so
          there is no second bill for a currency symbol to imply. */}
      <Stat label={t("admin.bigQueryUsage.kpi.slotHours", "Slot hours")}>
        <span className={stats.slotHours > 0 ? VALUE_CLASS : ZERO_VALUE_CLASS}>
          {stats.slotHours.toLocaleString(locale, {
            maximumFractionDigits: SLOT_HOURS_FRACTION_DIGITS,
          })}
        </span>
        <span className={SECONDARY_CLASS}>
          {t("admin.bigQueryUsage.kpi.slotHoursNote", "load, never billed")}
        </span>
      </Stat>

      <Stat label={t("admin.bigQueryUsage.kpi.p95", "p95")}>
        <span className={stats.p95DurationMs == null ? ZERO_VALUE_CLASS : VALUE_CLASS}>
          {stats.p95DurationMs == null ? DASH : formatPreciseDurationMs(stats.p95DurationMs)}
        </span>
      </Stat>

      {/* Its own figure rather than a row in the tenant table, so these queries
          are never absorbed into a tenant that did not run them. */}
      {stats.unattributedQueries > 0 ? (
        <button
          type="button"
          className={cn(
            STAT_CLASS,
            "rounded-sm outline-none hover:bg-muted/40 focus-visible:ring-1 focus-visible:ring-ring",
          )}
          aria-label={t(
            "admin.bigQueryUsage.kpi.unattributedAction",
            "Show only the {{queries}} queries with no tenant recorded",
            // Deliberately not `count`: that key would send i18next looking for
            // plural forms of a label that never needs them.
            { queries: stats.unattributedQueries.toLocaleString(locale) },
          )}
          onClick={onSelectUnattributed}
        >
          <span className={LABEL_CLASS}>
            {t("admin.bigQueryUsage.kpi.unattributed", "Unattributed")}
          </span>
          <span className="flex items-baseline gap-1.5">
            <span className={VALUE_CLASS}>
              {stats.unattributedQueries.toLocaleString(locale)}
            </span>
            <span className={SECONDARY_CLASS}>
              {t("admin.bigQueryUsage.kpi.unattributedNote", "no tenant recorded")}
            </span>
          </span>
        </button>
      ) : null}

      {hasTraffic ? null : (
        <span className={cn(SECONDARY_CLASS, "ml-auto px-1.5")}>
          {t("admin.bigQueryUsage.kpi.empty", "No queries ran in this window")}
        </span>
      )}
    </div>
  );
}
